import { cn, DialogTitle, DialogDescription } from '@bliks-io/ui';
import { type AppIcon } from '@/constants/entityIcons';

interface ContentHeaderProps {
  icon?: AppIcon;
  title: string;
  description?: string;
  variant?: 'minimal' | 'large';
  mode?: 'default' | 'dialog';
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
}

/**
 * ContentHeader component displays a title with an optional icon and description.
 * Used for page headers, section headers, modals, and other content areas.
 *
 * @param variant - Display variant: "minimal" (default, for list pages) or "large" (for modals)
 * @param mode - Rendering mode: "default" (uses HTML headings) or "dialog" (uses DialogTitle/DialogDescription for accessibility)
 * @param headingLevel - Semantic heading level (h1-h6). Defaults to h1 for minimal variant, h2 for large variant. Ignored when mode="dialog"
 *
 * @example
 * // Minimal variant (list pages)
 * ```tsx
 * <ContentHeader
 *   icon={Building2}
 *   title="Departments"
 *   description="Manage your organization's departments"
 * />
 * ```
 *
 * @example
 * // Large variant (modals)
 * ```tsx
 * <ContentHeader
 *   variant="large"
 *   icon={Building2}
 *   title="Edit Department"
 *   description="Update the details of this department"
 * />
 * ```
 *
 * @example
 * // Custom heading level for semantic HTML
 * ```tsx
 * <ContentHeader
 *   icon={Users}
 *   title="Team Members"
 *   description="Manage team members"
 *   headingLevel="h3"
 * />
 * ```
 *
 * @example
 * // Dialog mode for accessibility
 * ```tsx
 * <DialogHeader>
 *   <ContentHeader
 *     mode="dialog"
 *     variant="large"
 *     icon={Building2}
 *     title="Edit Department"
 *     description="Update the details"
 *   />
 * </DialogHeader>
 * ```
 */
export function ContentHeader({
  icon: Icon,
  title,
  description,
  variant = 'minimal',
  mode = 'default',
  headingLevel,
  className
}: ContentHeaderProps) {
  // Determine the title component based on mode and headingLevel
  const defaultHeadingLevel = variant === 'large' ? 'h2' : 'h1';
  const TitleComponent = mode === 'dialog' ? DialogTitle : headingLevel || defaultHeadingLevel;
  const DescriptionComponent = mode === 'dialog' ? DialogDescription : 'p';

  if (variant === 'large') {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        {Icon && (
          <div className="flex items-center justify-center bg-muted rounded-lg h-11 w-11">
            <Icon className="w-5 h-5 text-foreground" />
          </div>
        )}
        <div className="flex-1">
          <TitleComponent className="text-lg font-semibold">{title}</TitleComponent>
          {description && (
            <DescriptionComponent className="mt-1 text-sm text-muted-foreground">
              {description}
            </DescriptionComponent>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
        <TitleComponent className="text-xl font-semibold leading-7">{title}</TitleComponent>
      </div>
      {description && (
        <DescriptionComponent className="mt-1 text-sm font-normal leading-5 text-gray-8">
          {description}
        </DescriptionComponent>
      )}
    </div>
  );
}
